import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Lock } from "lucide-react";
import axios from "axios";
import { getGuide, GUIDES } from "../seoGuides";
import { CheckoutModal } from "../components/CheckoutModal";
import { Seo, SITE } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuidePage() {
  const { slug } = useParams();
  const g = getGuide(slug);
  const [price, setPrice] = useState("$29");
  const [checkout, setCheckout] = useState(false);

  useEffect(() => {
    if (!g) return;
    axios.get(`${API}/product`).then((r) => {
      if (r.data.amount != null) setPrice(new Intl.NumberFormat("en-US", { style: "currency", currency: (r.data.currency || "usd").toUpperCase(), minimumFractionDigits: 0 }).format(r.data.amount / 100));
    }).catch(() => {});
  }, [g, slug]);

  if (!g) return <Navigate to="/guides" replace />;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: g.h1,
      description: g.metaDescription,
      about: "digital privacy",
      author: { "@type": "Organization", name: "Ghost The Grid" },
      publisher: { "@type": "Organization", name: "Ghost The Grid", logo: { "@type": "ImageObject", url: `${SITE}/favicon.png` } },
      mainEntityOfPage: `${SITE}/guides/${slug}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: g.h1,
      description: g.intro,
      step: g.steps.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.h, text: s.body })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Privacy Guides", item: `${SITE}/guides` },
        { "@type": "ListItem", position: 3, name: g.h1, item: `${SITE}/guides/${slug}` },
      ],
    },
  ];

  const Cta = ({ testid }) => (
    <button data-testid={testid} onClick={() => setCheckout(true)}
      className="group relative inline-flex items-center gap-3 bg-accent text-white font-mono uppercase tracking-[0.15em] text-sm px-8 py-5 overflow-hidden">
      <span className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]" />
      <span className="relative z-10 flex items-center gap-2 group-hover:text-black transition-colors duration-300">
        <Lock size={14} /> Get the full pack, {price}
      </span>
    </button>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white noise-overlay">
      <Seo title={g.metaTitle} description={g.metaDescription} path={`/guides/${slug}`} type="article" jsonLd={jsonLd} />
      <header className="border-b border-white/10 sticky top-0 z-40 bg-[#050505]/90 backdrop-blur">
        <div className="max-w-[1000px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="guide-logo-link" className="font-display font-black uppercase tracking-tighter text-lg">Ghost The Grid</Link>
          <Link to="/" data-testid="guide-nav-pack" className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-accent transition-colors">Get The Pack →</Link>
        </div>
      </header>

      <article className="max-w-[760px] mx-auto px-6 py-16 lg:py-24">
        <Link to="/guides" className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent">// Privacy Guides</Link>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="mt-5 font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.95]">{g.h1}</motion.h1>
        <p className="mt-6 font-body text-lg text-white/60 leading-relaxed">{g.intro}</p>

        <div className="mt-12 space-y-8">
          {g.steps.map((s, i) => (
            <div key={i} data-testid={`guide-step-${i}`} className="border-l-2 border-accent/50 pl-6">
              <h2 className="font-display font-bold uppercase tracking-tight text-xl flex items-center gap-3">
                <span className="font-mono text-accent text-sm">{String(i + 1).padStart(2, "0")}</span>{s.h}
              </h2>
              <p className="mt-3 font-body text-white/60 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 border border-white/10 bg-[#0a0a0a] p-8 lg:p-10">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-accent">// The complete system</span>
          <h3 className="mt-4 font-display font-black uppercase tracking-tighter text-2xl sm:text-3xl leading-tight">Want the other 11 steps done for you?</h3>
          <p className="mt-4 font-body text-white/60 leading-relaxed">
            This is one node. The full Ghost The Grid pack walks you through all 12, with 30+ data-broker opt-outs and the exact settings for every device, step by step. Do it once, own it for life.
          </p>
          <ul className="mt-6 space-y-2">
            {["12 guided privacy nodes", "30+ broker opt-out links", "Every device, plain language", "One-time payment, yours forever"].map((b) => (
              <li key={b} className="flex items-center gap-3 font-body text-sm text-white/70"><Check size={16} className="text-accent" />{b}</li>
            ))}
          </ul>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
            <Cta testid="guide-buy-button" />
            <a href="/#free-tips" data-testid="guide-free-tips-link" className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45 hover:text-accent transition-colors underline underline-offset-4 decoration-white/20">
              Not ready? Get 5 free tips first →
            </a>
          </div>
        </div>

        <div className="mt-14">
          <h4 className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-5">// Related guides</h4>
          <div className="grid sm:grid-cols-2 gap-4">
            {g.related.map((rs) => {
              const r = GUIDES.find((x) => x.slug === rs);
              if (!r) return null;
              return (
                <Link key={rs} to={`/guides/${rs}`} data-testid={`related-${rs}`}
                  className="group border border-white/10 p-5 hover:border-accent transition-colors flex items-start justify-between gap-3">
                  <span className="font-body text-sm text-white/80 group-hover:text-white">{r.h1}</span>
                  <ArrowRight size={16} className="text-accent flex-shrink-0 mt-0.5" />
                </Link>
              );
            })}
          </div>
        </div>
      </article>

      <CheckoutModal open={checkout} lookupKey="gtg_pack_onetime" title="Privacy & Disappearance Pack" price={price} onClose={() => setCheckout(false)} />
    </div>
  );
}
